import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, Stack, Autocomplete, ListItemText } from '@mui/material';
import { Button } from 'components/index';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import CustomizedTextField from './CustomizedTextField';
import { RequiredFieldGap } from './project.util';
import { ProjectSummary } from './project.type';
import { getAllObservers, updateProjectSummary } from './project.api';
import { handleError } from '../../common/utils/util.errors';
import { UserDTO } from '../user-management/auth.type';

interface RequiredFieldsGateDialogProps {
  open: boolean;
  projectId: string;
  missingFields: RequiredFieldGap[];
  projectSummary: ProjectSummary;
  onSaved: (updatedSummary: ProjectSummary) => void;
  onCancel: () => void;
}

interface GateFormValues {
  projectTitle: string;
  projectNumber: string;
  bankName: string;
  observer: UserDTO | null;
}

/**
 * Blocks Magic Report entry until project title, project number, observer, and bank name are all
 * present — these can never legitimately be missing (title/number are required to create a project
 * in the first place), unlike the broader missing-field warnings shown on the Project page, which
 * are informational only. Saves directly to the Project via updateProjectSummary so the user never
 * has to leave the wizard entry flow to fix them.
 */
const RequiredFieldsGateDialog: React.FC<RequiredFieldsGateDialogProps> = ({
  open,
  projectId,
  missingFields,
  projectSummary,
  onSaved,
  onCancel,
}) => {
  const [availableObservers, setAvailableObservers] = useState<UserDTO[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const needs = (key: RequiredFieldGap['fieldKey']) => missingFields.some(field => field.fieldKey === key);

  useEffect(() => {
    if (!needs('observer')) return;
    const fetchObservers = async () => {
      try {
        const observers = await getAllObservers();
        setAvailableObservers(observers);
      } catch (error) {
        console.error('Failed to fetch observers:', error);
      }
    };
    fetchObservers();
  }, [open]);

  const formik = useFormik<GateFormValues>({
    initialValues: {
      projectTitle: '',
      projectNumber: '',
      bankName: '',
      observer: null,
    },
    enableReinitialize: true,
    validationSchema: yup.object({
      projectTitle: needs('projectTitle') ? yup.string().trim().required('Required') : yup.string(),
      projectNumber: needs('projectNumber') ? yup.string().trim().required('Required') : yup.string(),
      bankName: needs('bankName') ? yup.string().trim().required('Required') : yup.string(),
      observer: needs('observer') ? yup.object().nullable().required('Required') : yup.object().nullable(),
    }),
    onSubmit: async values => {
      setIsSaving(true);
      try {
        const payload: ProjectSummary = { ...projectSummary };
        if (needs('projectTitle')) payload.projectTitle = values.projectTitle.trim();
        if (needs('projectNumber')) payload.projectNumber = values.projectNumber.trim();
        // Client.AddressId is required server-side, so a project with no client yet needs an (empty) address sent alongside the name.
        if (needs('bankName')) {
          payload.client = {
            ...projectSummary.client,
            name: values.bankName.trim(),
            address: projectSummary.client?.address ?? { street: '', city: '' },
          };
        }
        if (needs('observer') && values.observer) payload.observer = values.observer;

        const updated = await updateProjectSummary(projectId, payload);
        toast.success('Project updated successfully!');
        onSaved(updated);
      } catch (error) {
        handleError(error);
      } finally {
        setIsSaving(false);
      }
    },
  });

  const canSubmit = formik.isValid && formik.dirty;

  return (
    <Dialog open={open} onClose={onCancel} maxWidth='sm' fullWidth disableRestoreFocus>
      <DialogTitle>Required Fields Missing</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          This project is missing information Magic Report requires. Fill it in below to continue.
        </DialogContentText>
        <form onSubmit={formik.handleSubmit}>
          <Stack spacing={2} py={1}>
            {needs('projectTitle') && (
              <CustomizedTextField
                name='projectTitle'
                label='Project Name'
                value={formik.values.projectTitle}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.projectTitle && Boolean(formik.errors.projectTitle)}
                helperText={formik.touched.projectTitle && formik.errors.projectTitle}
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
              />
            )}
            {needs('projectNumber') && (
              <CustomizedTextField
                name='projectNumber'
                label='Project Number'
                value={formik.values.projectNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.projectNumber && Boolean(formik.errors.projectNumber)}
                helperText={formik.touched.projectNumber && formik.errors.projectNumber}
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
              />
            )}
            {needs('observer') && (
              <Autocomplete
                options={availableObservers}
                getOptionLabel={option => `${option.firstName} ${option.lastName}`}
                value={formik.values.observer}
                onChange={(_, newValue) => formik.setFieldValue('observer', newValue)}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <ListItemText primary={`${option.firstName} ${option.lastName}`} />
                  </li>
                )}
                renderInput={params => (
                  <CustomizedTextField
                    {...params}
                    label='Observer'
                    onBlur={() => formik.setFieldTouched('observer', true)}
                    error={formik.touched.observer && Boolean(formik.errors.observer)}
                    helperText={formik.touched.observer && (formik.errors.observer as string)}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                )}
              />
            )}
            {needs('bankName') && (
              <CustomizedTextField
                name='bankName'
                label='Bank Name'
                value={formik.values.bankName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.bankName && Boolean(formik.errors.bankName)}
                helperText={formik.touched.bankName && formik.errors.bankName}
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
              />
            )}
          </Stack>
        </form>
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' color='error' onClick={onCancel}>
          Cancel
        </Button>
        <Button variant='contained' onClick={formik.submitForm} disabled={!canSubmit} color='primary' loading={isSaving}>
          Save &amp; Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RequiredFieldsGateDialog;
